import { ChevronRightIcon, ShieldCheckIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import initials from 'initials'
import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { JSONTree } from 'react-json-tree'

import {
  useAivyContext,
  useLanguageContext,
  useUserContext
} from '../../../context'
import { classNames, uuidSmall } from '../../../helpers'
import { ROLES } from '../../../helpers/authorization-roles'
import ModalEditUser from './modal-edit-user'

const ROLE_ORDER = ['ADMIN', 'MANAGER', 'WORKER', 'VIEWER']

const Users = ({ members, groupByRole, refetchUsers, allTags }) => {
  const { t } = useTranslation()
  const { activeSpace } = useUserContext()
  const { language } = useLanguageContext()
  const { adminInsights } = useAivyContext()

  const [editUser, setEditUser] = useState({})
  const [uuid, setUuid] = useState(uuidSmall())
  const [openUpdateUser, setOpenUpdateUser] = useState(false)

  const has_admin_role = activeSpace.authorization_role.id === ROLES.ADMIN.id

  const rows = groupByRole
    ? ROLE_ORDER.flatMap((role_id) => {
        const group = members.filter((member) => member.role_id === role_id)
        if (group.length === 0) return []

        return [{ isHeader: true, role_id, count: group.length }, ...group]
      })
    : members

  return (
    <>
      <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
        {rows.map((row, index) => {
          if (row.isHeader) {
            return (
              <div
                key={`header-${row.role_id}`}
                className={classNames(
                  'flex items-center gap-x-2 border-b border-gray-200 bg-gray-50 px-6 py-2.5',
                  index !== 0 && 'border-t'
                )}
              >
                <span className='text-[11px] font-bold uppercase tracking-wider text-gray-500'>
                  {t(ROLES[row.role_id].title)}
                </span>
                <span className='rounded-full border border-gray-200 bg-white px-2 text-[11px] font-semibold text-gray-400'>
                  {row.count}
                </span>
              </div>
            )
          }

          const {
            space_id,
            name,
            email,
            firstname,
            lastname,
            role_id,
            role_title,
            tags,
            last_seen,
            is_current_user
          } = row

          // admins can also edit themselves (tags only, role and
          // delete are locked for the own account inside the modal)
          const can_edit = has_admin_role

          return (
            <Fragment key={space_id}>
              {adminInsights && (
                <JSONTree
                  data={row.person}
                  shouldExpandNodeInitially={() => false}
                />
              )}
              <div
                className={classNames(
                  'flex gap-x-4 px-6 py-4',
                  index !== 0 && 'border-t border-gray-100',
                  can_edit && 'group cursor-pointer hover:bg-gray-50'
                )}
                onClick={
                  can_edit
                    ? () => {
                        setEditUser({
                          space_id,
                          email,
                          firstname,
                          lastname,
                          role_id,
                          tags,
                          is_current_user
                        })
                        // destroy the component for the new user
                        setUuid(uuidSmall())

                        setTimeout(() => setOpenUpdateUser(true), 0.05 * 1000)
                      }
                    : undefined
                }
              >
                <span className='relative inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-rockGrey'>
                  <span className='font-medium leading-none text-white'>
                    {initials(name)}
                  </span>
                  {is_current_user && (
                    <span className='absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-400 ring-2 ring-white' />
                  )}
                </span>
                <div className='min-w-0 flex-auto'>
                  <p className='text-sm font-semibold leading-6 text-gray-900'>
                    {name}
                  </p>
                  <p className='truncate text-xs leading-5 text-gray-500'>
                    {email}
                  </p>
                  {tags.length > 0 && (
                    <div className='mt-1.5 flex flex-wrap gap-1.5'>
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className='inline-flex items-center rounded-full border border-paleBlue bg-paleWhiteBlue px-2.5 py-0.5 text-[11px] font-medium text-darkBlue'
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className='flex flex-shrink-0 items-center gap-x-4'>
                  <div className='hidden sm:flex sm:flex-col sm:items-end sm:gap-y-1'>
                    <span
                      className={classNames(
                        'inline-flex items-center gap-x-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        role_id === ROLES.ADMIN.id
                          ? 'bg-paleWhiteBlue text-darkBlue'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {role_id === ROLES.ADMIN.id && (
                        <ShieldCheckIcon
                          className='h-3.5 w-3.5'
                          aria-hidden='true'
                        />
                      )}
                      {t(role_title)}
                    </span>
                    <span className='text-xs leading-5 text-gray-500'>
                      {is_current_user
                        ? 'Online'
                        : last_seen
                          ? formatDistanceToNow(new Date(last_seen), {
                              addSuffix: true,
                              locale: { de, en }[language]
                            })
                          : 'n/a'}
                    </span>
                  </div>
                  <ChevronRightIcon
                    className={clsx(
                      'h-5 w-5 flex-none text-gray-300 group-hover:text-gray-700',
                      !can_edit && 'opacity-0'
                    )}
                    aria-hidden='true'
                  />
                </div>
              </div>
            </Fragment>
          )
        })}

        {rows.length === 0 && (
          <div className='px-6 py-12 text-center'>
            <p className='text-sm text-gray-600'>
              {t('settings.user_management.empty_title')}
            </p>
            <p className='mt-1 text-xs text-gray-400'>
              {t('settings.user_management.empty_description')}
            </p>
          </div>
        )}
      </div>
      <ModalEditUser
        key={uuid}
        open={openUpdateUser}
        setOpen={setOpenUpdateUser}
        data={{ ...editUser, mode: 'update' }}
        suggestions={allTags}
        onSuccess={() => refetchUsers()}
      />
    </>
  )
}

Users.propTypes = {
  members: PropTypes.array.isRequired,
  groupByRole: PropTypes.bool,
  refetchUsers: PropTypes.func.isRequired,
  allTags: PropTypes.array
}

Users.defaultProps = {
  groupByRole: false,
  allTags: []
}

export default Users
